import {
    useContext, useMemo
} from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { SgPaymentUtils, SgNumberUtil, SgActiveTestsContext, } from 'saga-modules';
import CepConstants from 'sg-cep-constants';
import CommonConstants from 'sg-common-constants';
import metadata from './SgEndorsementPremiumDetails.metadata.json5';
import messages from './SgEndorsementPremiumDetails.messages';

function SgEndorsementPremiumDetails(props) {
    const {
        modelVM
    } = props;

    const {
        additionalPaymentInformation,
        transactionCost,
        paymentMethodCode,
        paymentPlanCode,
        isSagaCarInsurance
    } = modelVM.value;

    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);
    const { activeMvtTests } = useContext(SgActiveTestsContext);

    const additionalPaymentInfo = additionalPaymentInformation;
    const priceChanged = transactionCost.amount !== 0;
    const hasRefund = transactionCost.amount < 0;
    const showSeparatedPremiumAndInterests = additionalPaymentInfo.isMonthlyDD ? additionalPaymentInfo.isMonthlyDD && priceChanged : false;
    const isRefundLessThanOrEqualsFivePound = SgPaymentUtils.isRefundLessThanOrEqualsFivePound(transactionCost.amount);
    const isIncreaseLessThanOrEqualsFivePound = SgPaymentUtils.isIncreaseLessThanOrEqualsFivePound(transactionCost.amount);
    const isAnnualCCPayment = SgPaymentUtils.isAnnualCreditCardPayment(paymentPlanCode, paymentMethodCode);

    function calculateViewMode() {
        if (!priceChanged) {
            return CepConstants.PAYMENT_DISPLAY_TYPES.NONE;
        }
        if (SgPaymentUtils.isAnnualCreditCardPayment(paymentPlanCode, paymentMethodCode)) {
            return hasRefund && !isRefundLessThanOrEqualsFivePound
                ? CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_DECREASE_ANNUAL : CepConstants.PAYMENT_DISPLAY_TYPES.NONE;
        }
        if (hasRefund) {
            if (additionalPaymentInfo.hasUnpaidInvoices) {
                return additionalPaymentInfo.numberOfUnpaidInvoices > 1
                    ? CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_DECREASE_MORE_INSTALLMENTS_REMAINING
                    : CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_DECREASE_ONE_INSTALLMENT_REMAINING;
            }
            return CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_DECREASE_NO_REMAINING_INSTALLMENTS;
        }
        if (additionalPaymentInfo.hasUnpaidInvoices) {
            return additionalPaymentInfo.numberOfUnpaidInvoices > 1
                ? CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_INCREASE_MORE_INSTALLMENTS_REMAINING
                : CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_INCREASE_ONE_INSTALLMENT_REMAINING;
        }
        return CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_INCREASE_NO_REMAINING_INSTALLMENTS;
    }

    const paymentTypeViewMode = calculateViewMode();
    const showRefundAndInstalmentsSection = paymentTypeViewMode !== CepConstants.PAYMENT_DISPLAY_TYPES.NONE;

    const paymentInformation = useMemo(() => {
        switch (paymentTypeViewMode) {
            case CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_DECREASE_ANNUAL:
                return translator(messages.PriceDecreaseAnnual);
            case CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_DECREASE_MORE_INSTALLMENTS_REMAINING:
                return translator(messages.PriceDecreaseMoreInstalmentsRemaining,
                    {
                        nextInstalment: SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.firstCompensatingInstallment),
                        remainingInstalments: SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.newInstallmentAmount)
                    });
            case CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_DECREASE_ONE_INSTALLMENT_REMAINING:
                return translator(messages.PriceDecreaseOneInstalmentRemaining,
                    {
                        previousInstalment: SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.previousInstallmentAmount),
                        newInstalment: SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.newInstallmentAmount)
                    });
            case CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_INCREASE_MORE_INSTALLMENTS_REMAINING:
                return translator(messages.PriceIncreaseMoreInstalmentsRemaining,
                    {
                        nextInstalment: SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.firstCompensatingInstallment),
                        remainingInstalments: SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.newInstallmentAmount)
                    });
            case CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_INCREASE_ONE_INSTALLMENT_REMAINING:
                return translator(messages.PriceIncreaseOneInstalmentRemaining,
                    {
                        previousInstalment: SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.previousInstallmentAmount),
                        newInstalment: SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.newInstallmentAmount)
                    });
            case CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_DECREASE_NO_REMAINING_INSTALLMENTS:
                return translator(messages.PriceDecreaseNoInstalmentsRemaining);
            case CepConstants.PAYMENT_DISPLAY_TYPES.PRICE_INCREASE_NO_REMAINING_INSTALLMENTS:
                return translator(messages.PriceIncreaseNoInstalmentsRemaining);
            default:
                return '';
        }
    }, [paymentTypeViewMode, additionalPaymentInfo, intl, translator]);

    const costToPayContent = useMemo(() => {
        if (hasRefund) {
            return activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_5379)
                ? translator(messages.AmountToBeRefunded) : translator(messages.Refund);
        }
        return translator(messages.CostToPay);
    }, [hasRefund, translator, activeMvtTests]);

    const overrideProps = {
        priceChangeContainer: {
            visible: isAnnualCCPayment && (isRefundLessThanOrEqualsFivePound || isIncreaseLessThanOrEqualsFivePound)
        },
        priceChangeLabel: {
            content: isIncreaseLessThanOrEqualsFivePound ? translator(messages.IncreaseInPremium) : translator(messages.ReductionInPremium)
        },
        priceChangeAmount: {
            content: SgNumberUtil.getCurrencyValue(intl, transactionCost)
        },
        costToPay: {
            content: costToPayContent,
            className: activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_5379) ? 'sgPremiumLabel_WOC5379' : 'sgPremiumLabel'
        },
        transactionCost: {
            content: isAnnualCCPayment && (isIncreaseLessThanOrEqualsFivePound || isRefundLessThanOrEqualsFivePound) ? translator(messages.ZeroPounds)
                : SgNumberUtil.getCurrencyValue(intl, transactionCost, false, activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_5379))
        },
        underFiveInformation: {
            visible: isAnnualCCPayment && (isRefundLessThanOrEqualsFivePound || isIncreaseLessThanOrEqualsFivePound),
            content: isIncreaseLessThanOrEqualsFivePound ? translator(messages.NoCost) : translator(messages.NoRefund)
        },
        fullPremium: {
            content: translator(messages.FullPremium, {
                annualPremium: SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.totalAnnualPolicyPrem)
            })
        },
        sagaPlusRenewal: {
            visible: isSagaCarInsurance
        },
        monthlyCostContainer: {
            visible: showSeparatedPremiumAndInterests
        },
        monthlyPremiumChangeAmount: {
            content: additionalPaymentInfo.premiumChangeAmount ? SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.premiumChangeAmount) : null
        },
        monthlyInterestChangeLabel: {
            visible: additionalPaymentInfo.interestChangeAmount && additionalPaymentInfo.interestChangeAmount.amount !== 0,
        },
        monthlyInterestChangeAmount: {
            visible: additionalPaymentInfo.interestChangeAmount && additionalPaymentInfo.interestChangeAmount.amount !== 0,
            content: additionalPaymentInfo.interestChangeAmount
                ? SgNumberUtil.getCurrencyValue(intl, additionalPaymentInfo.interestChangeAmount) : null
        },
        refundAndInstalments: {
            visible: showRefundAndInstalmentsSection,
            content: paymentInformation
        }
    };

    const resolvers = {
        resolveCallbackMap: {},
        resolveComponentMap: {}
    };

    return renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
}

export default SgEndorsementPremiumDetails;
